import React, { useRef, useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import { useClaimHighlight } from 'src/hooks/useUIConfig';

interface ModelAnswer {
  model: string;
  answer: string;
}

interface Claim {
  id: string;
  claim: string;
  assessment: string;
  original_question: string;
  original_options: Record<string, string>;
  claimed_answer: string;
  model_answers: ModelAnswer[];
  consensus_answer: string;
  span_position?: [number, number];
  text_span?: string;
}

interface ShareData {
  originalText: string;
  claims: Claim[];
  requestId?: string;
}

interface ShareProps {
  isOpen: boolean;
  shareData: ShareData | null;
  onClose: () => void;
  onShare: () => void;
  renderUnderlinedText: (text: string, claims: Claim[]) => React.ReactNode;
}

// Define a type for Web Share Data
interface WebShareData {
  title?: string;
  text?: string;
  url?: string;
  files?: File[];
}

const Share: React.FC<ShareProps> = ({ isOpen, shareData, onClose, onShare, renderUnderlinedText }) => {
  const { shouldHighlight, getHighlightColor } = useClaimHighlight();
  const cardRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const [imageReady, setImageReady] = useState(false);
  const [imageData, setImageData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // Pre-load logo for canvas rendering
  useEffect(() => {
    // Create an image element to load the logo
    const logoImg = document.createElement('img') as HTMLImageElement;
    logoImg.crossOrigin = "anonymous"; // Enable CORS for the image
    
    // Set up load handlers
    logoImg.onload = () => {
      logoRef.current = logoImg;
    };
    
    logoImg.onerror = () => {
      console.error("Failed to load logo image");
    };
    
    // Start loading the image
    logoImg.src = "/white-logo.png";
  }, []);

  // Function to get the full text for sharing
  const getTextForSharing = useCallback(() => {
    if (!shareData) return "";
    return shareData.originalText;
  }, [shareData]);

  const handleDownload = useCallback(() => {
    if (imageData) {
      try {
        const link = document.createElement('a');
        link.href = imageData;
        link.download = 'mira-verification.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error("Download failed:", e);
        setError("Failed to download. Please try again.");
      }
    } else if (!isCapturing) {
      setError("Image not ready. Trying again...");
      // Not calling renderToCanvas() here to avoid direct circular dependency for useCallback
      // The user can click "Try again" button which calls renderToCanvas
    }
  }, [imageData, isCapturing, setError]);

  // Direct canvas drawing function (no html2canvas)
  const renderToCanvas = useCallback(() => {
    if (!shareData) return;
    
    try {
      setIsCapturing(true);
      setError(null);
      
      const canvas = canvasRef.current;
      if (!canvas) {
        setError("Canvas element not available");
        setIsCapturing(false);
        return;
      }
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setError("Could not get canvas context");
        setIsCapturing(false);
        return;
      }
      
      // Helper function to draw rounded rectangle
      const drawRoundedRect = (x: number, y: number, width: number, height: number, radius: number) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
      };
      
      // Set a temporary font for text measurements
      ctx.font = '20px Arial';
      
      // Dynamic sizing constants
      const maxWidth = 600;
      const borderWidth = 30;
      const paddingX = 25;
      const paddingY = 25;
      const lineHeight = 28;
      const footerHeight = 40;
      const textX = paddingX;
      const textY = paddingY + 15;
      const borderRadius = 8; // Updated to match rounded-lg (8px)
      
      // Get text dimensions through word wrapping calculation
      const text = getTextForSharing();
      const words = text.split(' ');
      let line = '';
      let y = textY;
      let lineCount = 1;
      const lines: string[] = [];
      
      // Calculate wrapped lines and count
      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > maxWidth - 2 * paddingX && i > 0) {
          lines.push(line);
          line = words[i] + ' ';
          lineCount++;
        } else {
          line = testLine;
        }
      }
      
      // Add the last line
      lines.push(line);
      
      // Calculate dynamic canvas height based on content (with space for green footer area)
      const contentHeight = lineCount * lineHeight + paddingY * 1.5;
      const whiteAreaBottomMargin = 16; // Reduced space for green background area (was 32)
      const totalHeight = contentHeight + whiteAreaBottomMargin + footerHeight;
      
      // Adjust canvas size
      const canvasWidth = maxWidth;
      const canvasHeight = Math.max(140, totalHeight);
      
      // Update canvas dimensions
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      // Set a clipping path for the entire canvas to a rounded rectangle
      ctx.save();
      drawRoundedRect(0, 0, canvasWidth, canvasHeight, borderRadius);
      ctx.clip();
      
      // Draw green background for entire canvas
      ctx.fillStyle = '#308F6A';
      drawRoundedRect(0, 0, canvasWidth, canvasHeight, borderRadius);
      ctx.fill();
      
      // Draw white content area with bottom margin (smaller than full canvas)
      const whiteAreaHeight = canvasHeight - whiteAreaBottomMargin - footerHeight;
      ctx.fillStyle = '#FFFFFF';
      drawRoundedRect(borderWidth/2, borderWidth/2, canvasWidth - borderWidth, whiteAreaHeight, borderRadius);
      ctx.fill();
      
      // Draw colored border with rounded corners
      const borderColor = '#308F6A';
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = borderWidth;
      drawRoundedRect(0, 0, canvasWidth, canvasHeight, borderRadius);
      ctx.stroke();
      
      // Prepare for drawing text
      ctx.fillStyle = '#111111';
      ctx.font = '20px Arial';
      
      // Reset for drawing
      y = textY;
      
      // Draw text with coloring based on claim assessments
      for (let i = 0; i < lines.length; i++) {
        const lineText = lines[i];
        const lineStart = lines.slice(0, i).join('').length;
        const lineEnd = lineStart + lineText.length;
        
        // First draw background highlights for claims in this line
        if (shareData.claims && shareData.claims.length > 0) {
          // Create a map to track highlighted positions for this line
          const highlightedPositions: Array<{start: number; end: number; claim: Claim}> = [];
          
          // Find all text_span matches in the full text
          for (const claim of shareData.claims) {
            if (!claim.text_span || !shouldHighlight(claim.assessment)) continue;
            
            // Find all occurrences of text_span in the original text
            let searchIndex = 0;
            while (searchIndex < text.length) {
              const index = text.indexOf(claim.text_span, searchIndex);
              if (index === -1) break;
              
              const spanEnd = index + claim.text_span.length;
              
              // Check if this occurrence overlaps with the current line
              if (spanEnd >= lineStart && index < lineEnd) {
                // Calculate position within the line
                const claimStartInLine = Math.max(0, index - lineStart);
                const claimEndInLine = Math.min(lineText.length, spanEnd - lineStart);
                
                if (claimStartInLine < claimEndInLine) {
                  highlightedPositions.push({
                    start: claimStartInLine,
                    end: claimEndInLine,
                    claim
                  });
                }
              }
              
              searchIndex = index + 1;
            }
          }
          
          // Sort by start position and remove overlaps
          highlightedPositions.sort((a, b) => a.start - b.start);
          const nonOverlapping: typeof highlightedPositions = [];
          let lastEnd = -1;
          
          for (const pos of highlightedPositions) {
            if (pos.start >= lastEnd) {
              nonOverlapping.push(pos);
              lastEnd = pos.end;
            }
          }
          
          // Draw highlights for non-overlapping positions
          for (const { start, end, claim } of nonOverlapping) {
            const claimSegment = lineText.substring(start, end);
            if (!claimSegment.trim()) continue;
            
            // Measure positioning
            const beginningWidth = ctx.measureText(lineText.substring(0, start)).width;
            const claimWidth = ctx.measureText(claimSegment).width;
            
            // Get highlight color based on assessment and global setting
            const highlightColor = getHighlightColor(claim.assessment);
            
            // Set background color based on assessment
            if (highlightColor === 'green') {
              ctx.fillStyle = 'rgba(34, 197, 94, 0.3)'; // Muted green with lower opacity for text visibility
            } else if (highlightColor === 'red') {
              ctx.fillStyle = 'rgba(239, 68, 68, 0.3)'; // Muted red with lower opacity for text visibility
            } else {
              // NO CONSENSUS or other status
              ctx.fillStyle = 'rgba(156, 163, 175, 0.3)'; // Muted gray for no consensus
            }
            
            // Draw the background highlight
            ctx.fillRect(
              textX + beginningWidth, // x position based on text measurement
              y - 17, // slightly above the baseline
              claimWidth, // width of the exact claim text
              22 // height of highlight
            );
            
            // Draw a subtle underline
            if (highlightColor === 'green') {
              ctx.strokeStyle = '#38A169';
            } else if (highlightColor === 'red') {
              ctx.strokeStyle = '#E53E3E';
            } else {
              ctx.strokeStyle = '#a3a3a3'; // gray-700 for NO CONSENSUS
            }
            ctx.lineWidth = 1.5; // Increased from 1 for better visibility
            ctx.beginPath();
            ctx.moveTo(textX + beginningWidth, y + 5); // Position at bottom of background rectangle
            ctx.lineTo(textX + beginningWidth + claimWidth, y + 5); // Position at bottom of background rectangle
            ctx.stroke();
          }
        }
        
        // Then draw the text on top
        ctx.fillStyle = '#111111';
        ctx.fillText(lineText, textX, y);
        
        // Move to next line
        y += lineHeight;
      }
      
      // Restore context after clipping
      ctx.restore();
      
      // Draw "verified by mira" text in the green background area
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px Arial';
      
      const verifiedText = 'verified by';
      const textWidth = ctx.measureText(verifiedText).width;
      
      // Position text in the green background area (bottom left)
      const textBottomY = canvasHeight - footerHeight/2 + 6;
      const textLeftX = 24; // Match left-3 positioning from HTML
      
      ctx.fillText(verifiedText, textLeftX, textBottomY);

      // Draw the mira logo if available
      if (logoRef.current) {
        // Calculate position: textWidth + some padding after the "verified by" text
        // Use consistent positioning with the text
        const logoX = textLeftX + textWidth + 5;
        const logoY = textBottomY - 13; // Better vertical alignment
        const logoWidth = 49;
        const logoHeight = 15;
        
        // Draw the logo
        ctx.drawImage(logoRef.current, logoX, logoY, logoWidth, logoHeight);
      } else {
        // Fallback if logo isn't loaded: just write "mira"
        ctx.fillText('mira', textLeftX + textWidth + 5, textBottomY);
      }
      
      // Get image data
      const imgData = canvas.toDataURL('image/png');
      setImageData(imgData);
      setImageReady(true);
      setIsCapturing(false);

      // Auto download the image when ready
      setTimeout(() => {
        // Convert the image to a Blob for sharing
        fetch(imgData)
          .then(res => res.blob())
          .then(() => {
            // Auto download only if sharing isn't available
            if (typeof navigator.share === 'undefined') {
              handleDownload();
            }
          });
      }, 300);
      
    } catch (error) {
      console.error("Error rendering to canvas:", error);
      setError("Failed to create image. Please try again.");
      setIsCapturing(false);
    }
  }, [shareData, shouldHighlight, getHighlightColor, getTextForSharing, handleDownload, canvasRef, logoRef, setError, setImageData, setImageReady, setIsCapturing]);

  const handleShare = async () => {
    if (!imageData || !shareData) {
      setError("Image not ready yet. Please wait.");
      return;
    }

    try {
      // Convert data URL to blob
      const response = await fetch(imageData);
      const blob = await response.blob();
      
      // Create a File object from the Blob
      const imageFile = new File([blob], 'mira-verification.png', { type: 'image/png' });
      
      // Base share data
      const webShareData: WebShareData = {
        title: 'Verified by Mira',
      };
      
      // Check if files can be shared. If so, only share the image.
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [imageFile] })) {
        webShareData.files = [imageFile];
      } else {
        // Fallback to sharing text if image cannot be shared
        const shareText = `${getTextForSharing()}\n\nVerified by Mira`;
        webShareData.text = shareText;
      }
      
      // Attempt to use Web Share API
      if (typeof navigator.share === 'function') {
        await navigator.share(webShareData);
        onShare(); // Call the original onShare callback
      } else {
        // Fallback for devices without sharing capability
        handleDownload();
        setError("Native sharing not available on this device. Image downloaded instead.");
      }
    } catch (error) {
      console.error("Error sharing:", error);
      setError("Sharing failed. Downloading image instead.");
      handleDownload();
    }
  };

  // Render canvas when component is loaded
  useEffect(() => {
    if (isOpen && shareData) {
      const timeoutId = setTimeout(() => {
        renderToCanvas();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, shareData, renderToCanvas]);

  if (!isOpen || !shareData) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-4">
          <h3 className="text-md font-medium">Share verified claim on twitter </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4">
          {/* This is shown to the user */}
          <div ref={cardRef} className="rounded-lg overflow-hidden border-[12px] border-[#308F6A] relative bg-[#308F6A]">
            {/* White content area with margin bottom to leave space for text */}
            <div className="rounded-lg p-6 bg-white mb-8">
              <p className="text-sm text-[#111] leading-relaxed font-medium">
                {renderUnderlinedText(getTextForSharing(), shareData.claims)}
              </p>
            </div>
            
            {/* Verified by text positioned in the green background area */}
            <div className="absolute bottom-1 left-3 text-white">
              <span style={{fontFamily:'gt-super'}} className="text-sm flex items-center gap-1">
                verified by
                <span className="flex items-center">
                  <Image src="/white-logo.png" alt="mira" width={49} height={15} className="inline-block" />
                </span>
              </span>
            </div>
          </div>
          
          {/* Hidden canvas that will be used to generate the image */}
          <canvas 
            ref={canvasRef} 
            style={{ display: 'none' }} 
            width="600" 
            height="300"
          ></canvas>

          {error && (
            <div className="mt-3 p-2 bg-red-50 text-red-700 text-sm rounded-md">
              {error}
              <button 
                onClick={renderToCanvas}
                className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
              >
                Try again
              </button>
            </div>
          )}
        </div>
        
        <div className="flex p-3 gap-3">
          <button 
            onClick={handleShare}
            disabled={isCapturing || !imageReady}
            className={`flex-1 py-3 text-center text-sm font-medium text-white ${(isCapturing || !imageReady) ? 'bg-gray-500' : 'bg-[#111827] hover:bg-black'} rounded`}
          >
            {isCapturing ? "Preparing..." : "Share"}
          </button>
          <button 
            onClick={onClose}
            className="flex-1 py-3 text-center text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Share; 